import React, { useEffect, useState } from 'react'
import { useLocation } from '@fs/zion-router'
import getPermission from '@fs/zion-permissions'
import { LayoutBand, useContainerWidth } from '@fs/zion-ui'
import { i18n } from '@fs/zion-locale'
import { css } from '@emotion/react'
import { useFeatureFlag } from '@fs/zion-flags'
import { geoData } from '@fs/zion-config'

import { RenderZionCMSItems } from '../../utils/RenderZionCMSItem/RenderZionCMSItem'
import Separator from '../Separator'
import { TextTheme } from '../_helper-components'
import AlignmentContext from './SectionContext'
import useResponsiveValues from '../../hooks/useResponsiveValues'
import useDebounce from '../../utils/useDebounce'
import SectionBackground from './SectionBackground'

type SectionProps = ZionCMSSection & {
  children?: React.ReactNode
}

const innerContentCss = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;

  &[content-alignment='center'] {
    align-items: center;
  }
  &[content-alignment='right'] {
    align-items: flex-end;
  }
`

const DEFAULT_WIDTHS = {
  xxs: false,
  xs: false,
  sm: false,
  md: false,
  lg: false,
  xl: false,
  xxl: false,
}

const PERM_INFO = {
  LDS: {
    context: 'CrxCtxPublicAccess',
    perm: 'ThemisPrmLdsMember',
  },
  FHC: {
    context: 'CrxCtxPublicAccess',
    perm: 'ThemisPrmFhlc',
  },
  REG_PAT: {
    context: 'CrxCtxPublicAccess',
    perm: 'ThemisPrmRegisteredPatron',
  },
  AFFILIATE: {
    context: 'CrxCtxPublicAccess',
    perm: 'ThemisPrmAffiliate',
  },
  DISCOVERY: {
    context: 'CrxCtxPublicAccess',
    perm: 'ThemisPrmDiscoveryCenter',
  },
}

/**
 * This is the public, exported Section interface.
 */
export default function Section(props: SectionProps): JSX.Element {
  const { sectionWidths = '', permissions = '', locales = '', locations = '', splitIOFlagName = '' } = props
  // If we don't have any constraints then just go straight to the standard section (InternalSection)
  const SectionTag =
    sectionWidths.length > 0 ||
    permissions.length > 0 ||
    locales.length > 0 ||
    locations.length > 0 ||
    splitIOFlagName.length > 0
      ? ConstrainedSection
      : InternalSection
  return <SectionTag {...props} />
}

/**
 * This is the Section used if there are permission or Split I/O flag restrictions on the section element.
 */
function ConstrainedSection(props: SectionProps): JSX.Element {
  const {
    sectionWidths = '',
    permissions = '',
    locales = '',
    locations = '',
    splitIOFlagName = '',
    splitIOFlagValue = false,
  } = props
  const [widthOK, setWidthOK] = useState(false)
  const atWidth = useContainerWidth()
  const { search } = useLocation()
  const [permissionOK, setPermissionOK] = useState(false)
  const [localesOK, setLocalesOK] = useState(false)
  const [locationsOK, setLocationsOK] = useState(false)
  const featureFlagValue = useFeatureFlag(splitIOFlagName) || { isOn: false }
  const [flagOK, setFlagOK] = useState(false)
  const urlSearchParams = new URLSearchParams(search)
  const previewId = urlSearchParams.get('previewId') || undefined
  const debounce = useDebounce(window.innerWidth, 500)

  useEffect(() => {
    const widthsArray = sectionWidths.length > 0 ? sectionWidths.split(', ') : null
    if (!widthsArray) {
      // No need to check because we aren't constrained by width
      setWidthOK(true)
    } else {
      const desiredWidths = JSON.parse(JSON.stringify(DEFAULT_WIDTHS))
      widthsArray.forEach((w) => {
        desiredWidths[w] = true
      })
      setWidthOK(atWidth(desiredWidths))
    }
  }, [sectionWidths, atWidth, debounce])

  useEffect(() => {
    const permissionsArray = permissions.length > 0 ? permissions.split(', ') : null
    if (permissionsArray) {
      permissionsArray.forEach(async (value) => {
        if (value.startsWith('NOT_')) {
          const auth = await getPermission(PERM_INFO[value.slice(4)].perm, PERM_INFO[value.slice(4)].context)
          if (!auth.authorized) {
            setPermissionOK(true)
          }
        } else {
          const auth = await getPermission(PERM_INFO[value].perm, PERM_INFO[value].context)
          if (auth.authorized) {
            setPermissionOK(true)
          }
        }
      })
    }
  }, [permissions])

  useEffect(() => {
    const localesArray = locales.length > 0 ? locales.split(', ') : null
    if (localesArray && localesArray.find((locale) => locale === i18n.language)) {
      setLocalesOK(true)
    }
  }, [locales])

  useEffect(() => {
    const locationsArray = locations.length > 0 ? locations.split(', ') : null
    if (geoData?.country && locationsArray && locationsArray.find((country) => country === geoData.country)) {
      setLocationsOK(true)
    }
  }, [locations])

  useEffect(() => {
    if (splitIOFlagName) {
      setFlagOK(Boolean(splitIOFlagValue) === featureFlagValue.isOn)
    }
  }, [splitIOFlagName, splitIOFlagValue, featureFlagValue.isOn])

  if (previewId) {
    let msg = ''
    if (permissions.length > 0) {
      msg += `Permissions: ${permissions.replace('LDS', 'Church Member')} `
    }
    if (sectionWidths.length > 0) {
      msg += `Widths: ${sectionWidths}`
    }
    if (locales.length > 0) {
      msg += `Locales: ${locales}`
    }
    if (locations.length > 0) {
      msg += `Locations: ${locations}`
    }
    if (splitIOFlagName.length > 0) {
      msg += `${splitIOFlagName}: ${splitIOFlagValue ? 'on' : 'off'} `
    }
    return (
      <PreviewDetail msg={msg}>
        <InternalSection {...props} />
      </PreviewDetail>
    )
  }

  if (
    (permissions.length > 0 && !permissionOK) ||
    (splitIOFlagName.length > 0 && !flagOK) ||
    (locales.length > 0 && !localesOK) ||
    (locations.length > 0 && !locationsOK) ||
    (sectionWidths.length > 0 && !widthOK)
  ) {
    return <></>
  }

  // If we get here we either aren't restricted or we are restricted but are permitted...
  return <InternalSection {...props} />
}

function PreviewDetail({ msg = 'preview', children }): JSX.Element {
  return (
    <div style={{ border: 'dashed 3px fuchsia', borderRadius: '5px', position: 'relative', marginTop: '10px' }}>
      <div
        style={{
          position: 'absolute',
          top: '0px',
          insetInlineEnd: '5px',
          marginTop: '-16px',
          padding: '0 5px',
          background: 'lime',
        }}
      >
        {msg}
      </div>
      {children}
    </div>
  )
}

/**
 * This is the internal section element that actually displays the section content. This is not intended to be invoked
 * directly by rather through the exported <Section> element.
 */
function InternalSection({ background, modules, textColors, layout, children }: SectionProps): JSX.Element {
  const { bottomSpacing, contentMaxWidth, horizontalAlignment, minimumHeightPixels, topSpacing, verticalAlignment } =
    useResponsiveValues(layout)
  const { theme: textTheme } = textColors || {}

  return (
    <SectionBackground {...background}>
      <LayoutBand
        minHeight={!modules?.length ? minimumHeightPixels || 500 : minimumHeightPixels}
        maxWidth={contentMaxWidth}
        data-testid="zion-cms-section"
        alignY={verticalAlignment}
      >
        <Separator size={topSpacing} />
        <TextTheme theme={textTheme}>
          <div css={innerContentCss} content-alignment={horizontalAlignment}>
            <AlignmentContext.Provider value={horizontalAlignment}>
              <RenderZionCMSItems items={modules} />
            </AlignmentContext.Provider>
            {children}
          </div>
        </TextTheme>
        <Separator size={bottomSpacing} />
      </LayoutBand>
    </SectionBackground>
  )
}
